import React, { useEffect, useState } from "react";
import axios from "axios";
import { FileText, Download, ArrowUpRight, Check, X } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { SERVICES } from "@/data/services";
import { getUtmAttribution } from "@/utils/utm";
import { Link } from "react-router-dom";
import { NewsletterOptIn } from "@/components/HealthCheck";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RESOURCES = [
  {
    slug: "hvac-compliance-checklist-2026",
    tag: "Compliance",
    title: "Commercial HVAC Compliance Checklist 2026",
    strap: "Every statutory item a UK FM should have signed off — with the deadline attached to each.",
    bullets: ["F-Gas, TR/19, BS 9999, TM44, PSSR", "Top 5 gaps we find on first visit", "Free 30-minute compliance audit offer"],
    pages: "2 pages · A4",
  },
  {
    slug: "annual-maintenance-planner",
    tag: "Planning",
    title: "Facilities Manager's Annual Maintenance Planner",
    strap: "A month-by-month schedule that keeps your HVAC estate compliant, comfortable and cheap to run.",
    bullets: ["Q1-Q4 task calendar", "Budget rule of thumb for a 10k sq ft site", "Ad-hoc + emergency SLAs"],
    pages: "3 pages · A4",
  },
  {
    slug: "tr19-kitchen-extract-explainer",
    tag: "Kitchen extract",
    title: "TR/19 Kitchen Extract Compliance, Explained",
    strap: "What the standard actually requires, how often to clean, and the four documents your insurer will ask for.",
    bullets: ["Cleaning frequency table (2h – 24h operations)", "The four documents you should have on file", "Common failures on first visit"],
    pages: "2 pages · A4",
  },
  {
    slug: "credentials-at-a-glance",
    tag: "Onboarding",
    title: "Calmbreeze Credentials at a Glance",
    strap: "Everything a supplier compliance team needs to onboard Calmbreeze — on one page.",
    bullets: ["Legal entity, insurance summary, certifications", "Services offered & areas served", "Recent project references"],
    pages: "2 pages · A4",
  },
];

const Resources = () => {
  const [gate, setGate] = useState(null); // resource object

  return (
    <>
      <Seo
        title="Free Compliance Resources — Calmbreeze"
        description="Download free HVAC compliance checklists, kitchen extract explainers and annual maintenance planners for UK facilities managers."
        path="/resources"
      />

      <section className="pt-32 md:pt-40 pb-16 bg-paper">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <Reveal>
            <div className="font-mono text-[11px] tracking-overline uppercase text-accent mb-4">[ Free resources ]</div>
            <h1 className="font-display font-bold text-[2.5rem] md:text-[3.75rem] leading-[1.02] tracking-tightest max-w-3xl">
              Compliance guides for busy Facilities Managers.
            </h1>
            <p className="text-[16px] md:text-[18px] text-muted-foreground max-w-2xl mt-6 leading-relaxed">
              Straight-talking PDFs written by our engineers, covering the compliance items that actually keep insurers happy and buildings safe.
              Enter your details once — we&apos;ll email you the file plus you can save it as PDF directly.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24 md:pb-32 bg-paper">
        <div className="max-w-editorial mx-auto px-6 md:px-10 lg:px-12">
          <div className="grid md:grid-cols-2 gap-6 md:gap-8" data-testid="resources-grid">
            {RESOURCES.map((r, i) => (
              <Reveal key={r.slug} delay={i * 60}>
                <article
                  className="h-full flex flex-col bg-white border border-black/8 rounded-md p-7 md:p-8 hover:shadow-[0_20px_60px_rgba(0,0,0,0.08)] transition-shadow"
                  data-testid={`resource-card-${r.slug}`}
                >
                  <div className="flex items-center justify-between mb-6">
                    <span className="font-mono text-[10px] tracking-overline uppercase text-accent">[ {r.tag} ]</span>
                    <span className="text-[11px] text-muted-foreground font-mono">{r.pages}</span>
                  </div>
                  <FileText className="h-7 w-7 text-brand mb-6" strokeWidth={1.5} />
                  <h2 className="font-display font-bold text-[1.35rem] md:text-[1.55rem] leading-tight tracking-tight mb-3">
                    {r.title}
                  </h2>
                  <p className="text-[14px] text-muted-foreground leading-relaxed mb-5">{r.strap}</p>
                  <ul className="text-[13px] text-ink/80 space-y-2 mb-8">
                    {r.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2">
                        <Check className="h-4 w-4 mt-0.5 text-brand shrink-0" strokeWidth={2.5} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <button
                    onClick={() => setGate(r)}
                    data-testid={`resource-open-${r.slug}`}
                    className="mt-auto self-start inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white text-[12px] font-semibold hover:bg-brand transition"
                  >
                    <Download className="h-4 w-4" />
                    Download free
                  </button>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {gate && <ResourceGate resource={gate} onClose={() => setGate(null)} />}
    </>
  );
};

// -------- Gate form modal --------
const ResourceGate = ({ resource, onClose }) => {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company: "",
    service_interest: "",
    website: "",
    newsletter_opt_in: false,
  });
  const [status, setStatus] = useState("idle"); // idle | sending | done | error
  const [error, setError] = useState("");
  const [downloadUrl, setDownloadUrl] = useState("");

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      const attribution = getUtmAttribution();
      const { data } = await axios.post(`${API}/resources/download`, {
        ...form,
        resource_slug: resource.slug,
        source: "resource_gate",
        ...attribution,
      });
      setDownloadUrl(data.download_url);
      setStatus("done");
      // Open the file in a new tab automatically
      window.open(data.download_url, "_blank", "noopener,noreferrer");
      // Suppress exit-intent for anyone who's already converted here
      try { localStorage.setItem("cb_exit_intent_converted", "1"); } catch { /* ignore */ }
    } catch (e2) {
      setStatus("error");
      setError(e2?.response?.data?.detail || e2?.message || "Something went wrong");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      role="dialog"
      aria-label="Download resource"
      data-testid="resource-gate"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-md w-full max-w-lg shadow-2xl relative">
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="resource-gate-close"
          className="absolute top-4 right-4 p-2 hover:bg-black/5 rounded"
        >
          <X className="h-5 w-5" />
        </button>

        {status !== "done" && (
          <>
            <div className="px-6 pt-6 pb-4 border-b border-black/8">
              <div className="font-mono text-[10px] tracking-overline uppercase text-accent">[ {resource.tag} · Free download ]</div>
              <div className="font-display font-bold text-[1.25rem] leading-tight mt-2">{resource.title}</div>
              <div className="text-[12px] text-muted-foreground mt-2">One tap. We&apos;ll email you a copy too.</div>
            </div>
            <form onSubmit={submit} className="p-6 space-y-3" data-testid="resource-form">
              <div className="grid grid-cols-2 gap-3">
                <input required placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand" data-testid="resource-first" />
                <input required placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand" data-testid="resource-last" />
              </div>
              <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand" data-testid="resource-email" />
              <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand" data-testid="resource-company" />
              <input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand" data-testid="resource-phone" />
              <select value={form.service_interest} onChange={(e) => setForm({ ...form, service_interest: e.target.value })} className="w-full px-3 py-2 border border-black/12 rounded text-[14px] focus:outline-none focus:border-brand bg-white" data-testid="resource-service">
                <option value="">Which service are you thinking about? (optional)</option>
                {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
              </select>
              {/* Honeypot */}
              <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" aria-hidden="true" />
              <NewsletterOptIn checked={form.newsletter_opt_in} onChange={(v) => setForm({ ...form, newsletter_opt_in: v })} testId="resource-newsletter-optin" />
              {error && <div className="text-[13px] text-red-600 font-mono bg-red-50 border border-red-100 rounded px-3 py-2">{error}</div>}
              <button type="submit" disabled={status === "sending"} data-testid="resource-submit" className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-accent hover:bg-accent-hover text-white text-[13px] font-semibold transition disabled:opacity-50">
                <Download className="h-4 w-4" />
                {status === "sending" ? "Preparing your download…" : "Get the free download"}
              </button>
              <div className="text-[11px] text-muted-foreground text-center pt-1">By downloading you consent to Calmbreeze contacting you about your enquiry. We never share your details.</div>
            </form>
          </>
        )}

        {status === "done" && (
          <div className="p-8 text-center" data-testid="resource-gate-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-brand/10 flex items-center justify-center text-brand mb-4">
              <Check className="h-7 w-7" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-[1.35rem] mb-2">Your download is ready</div>
            <p className="text-[13px] text-muted-foreground mb-6">
              If it didn&apos;t open automatically, click below. We&apos;ve also emailed a copy to your inbox.
            </p>
            <a
              href={downloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="resource-download-link"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-brand text-white text-[13px] font-semibold hover:bg-brand-hover transition"
            >
              Open {resource.title} <ArrowUpRight className="h-4 w-4" />
            </a>
            <div className="mt-6 pt-6 border-t border-black/8">
              <p className="text-[13px] text-muted-foreground mb-3">Want us to check your systems against it?</p>
              <Link
                to="/?book=1"
                onClick={() => { window.gtag?.("event", "book_survey_click", { location: "download_success" }); onClose(); }}
                data-testid="resource-book-survey"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-accent text-white text-[13px] font-semibold hover:bg-accent-hover transition"
              >
                Book a free site survey <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
            <div className="mt-6">
              <button onClick={onClose} className="text-[13px] font-semibold text-ink/60 hover:text-ink border-b border-ink/20 pb-0.5">Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Resources;
